<template>
  <busy-area v-if="isAllowedToSee" :busy="loading">
    <div class="flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <div class="flex flex-col justify-stretch">
        <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
          {{ $t('pages.driver.payoutList.statisticsHeader1') }}
        </div>
        <table class="statisticsTable h-full">
          <thead>
            <tr>
              <th class="font-normal">
                (in CHF)
              </th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.total2') }}</th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.filtered') }}</th>
              <th v-if="!hideSelected">
                {{ $t('pages.driver.payoutList.paymentStatistic.selected') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.pending') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perStatus?.pending ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perStatus?.pending ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perStatus?.pending) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.inPayment') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perStatus?.inPayment ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perStatus?.inPayment ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perStatus?.inPayment) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.payed') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perStatus?.payed ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perStatus?.payed ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perStatus?.payed) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.error') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perStatus?.error ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perStatus?.error ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perStatus?.error) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.total') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.total ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.total ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.total) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex flex-col justify-stretch">
        <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
          {{ $t('pages.driver.payoutList.statisticsHeader2') }}
        </div>
        <table class="statisticsTable h-full">
          <thead>
            <tr>
              <th class="font-normal">
                (in CHF)
              </th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.total2') }}</th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.filtered') }}</th>
              <th v-if="!hideSelected">
                {{ $t('pages.driver.payoutList.paymentStatistic.selected') }}
              </th>
              <th v-if="statistics.max">
                {{ $t('pages.driver.payoutList.paymentStatistic.max') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.week') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.week ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.week ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.week) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.week, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.reward') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.reward ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.reward ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.reward) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.reward, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.box') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.box ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.box ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.box) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.box, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.depot') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.depot ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.depot ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.depot) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.depot, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.credit') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.credit ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.credit ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.credit) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.credit, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.debt') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.debt ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.debt ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.debt) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.debt, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.manual') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.manual ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.manual ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.manual) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.perType?.manual, false, '-') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.total') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.total ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.total ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.total ?? 0) }}
              </td>
              <td v-if="statistics.max">
                {{ formatNumberForCurrency(statistics.max?.total ?? 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!hideRewards" class="flex flex-col justify-stretch">
        <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
          {{ $t('pages.driver.payoutList.statisticsHeader3') }}
        </div>
        <table class="statisticsTable h-full">
          <thead>
            <tr>
              <th class="font-normal">
                (in CHF)
              </th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.total2') }}</th>
              <th>{{ $t('pages.driver.payoutList.paymentStatistic.filtered') }}</th>
              <th v-if="!hideSelected">
                {{ $t('pages.driver.payoutList.paymentStatistic.selected') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.reward1') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perRewardType?.[1] ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perRewardType?.[1] ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perRewardType?.[1]) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.reward2') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perRewardType?.[2] ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perRewardType?.[2] ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perRewardType?.[2]) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.reward3') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perRewardType?.[3] ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perRewardType?.[3] ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perRewardType?.[3]) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('pages.driver.payoutList.paymentStatistic.total') }}</td>
              <td>{{ formatNumberForCurrency(statistics?.total?.perType?.reward ?? 0) }}</td>
              <td>{{ formatNumberForCurrency(statistics?.filtered?.perType?.reward ?? 0) }}</td>
              <td v-if="!hideSelected">
                {{ formatNumberForCurrency(statistics.selected?.perType?.reward ?? 0) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </busy-area>
</template>

<script lang="ts" setup>
import BusyArea from '@utils/busy/BusyArea.vue';
import { computed } from 'vue';
import { formatNumberForCurrency } from '@/utils/helpers';
import { useStore } from '@/store';

interface StatisticsItem {
  total?: number;
  perStatus?: {
    pending: number;
    inPayment: number;
    payed: number;
    error: number;
  };
  perType?: {
    week: number;
    reward: number;
    box: number;
    depot: number;
    manual: number;
    credit: number;
    debt: number;
  };
  perRewardType?: {
    1: number;
    2: number;
    3: number;
  };
}

export interface Statistics {
  selected: StatisticsItem;
  filtered: StatisticsItem;
  max?: StatisticsItem;
  total: StatisticsItem;
}

interface Props {
  statistics: Statistics;
  loading?: boolean;
  hideSelected?: boolean;
  hideRewards?: boolean;
  adminsAreAllowed?: boolean;
}

const props = defineProps<Props>();

const auth = useStore();
const isAllowedToSee = computed(() => props.adminsAreAllowed ? auth.getters['auth/IS_ADMIN'] : auth.getters['auth/IS_FINANCE_ADMIN']);
</script>

<style lang="scss" scoped>
.statisticsTable {
  @apply border;

  tr {
    td, th {
      &:not(:last-child) {
        @apply border-r;
      }
      @apply px-5 py-2;
      text-align: right;
    }

    th {
      @apply border-b;
    }

    &:last-child {
      td:not(:first-child) {
        border-top: 3px double black;
      }
    }
  }
}
</style>
